/**
 * N1 (Paketplan v4.1): dauerhafter Gerätespeicher der Kassen-App mit echten Transaktionen.
 *
 * - **IndexedDB** (Datenbank `bilanx-kassa`, Version 2) – dieselbe Datenbank wie der
 *   Offline-Puffer. Bereiche: `zaehler` (Belegnummernzähler je Kasse), `vorgang`
 *   (Vorgangsdatensätze vor dem ersten Sendeversuch), `meta` (Gerätekennung, Lease der
 *   Synchronisation, Ausfallabschluss-Merker). Eine Transaktion ist über alle Fenster
 *   (Tabs) derselben Installation atomar.
 * - **Rückfall `localStorage`** (nur ohne IndexedDB): gleiche Schnittstelle, aber nicht über
 *   Fenster atomar – deshalb verkauft eine solche Installation nur online und puffert nicht
 *   (Paketplan N1.6). Je Installation gibt es genau eine Speicherart.
 *
 * Ein alter, noch geöffneter Tab kann das Datenbank-Upgrade blockieren: dann scheitert das
 * Öffnen mit `SpeicherBlockiert`, und die Kasse verkauft nicht, bis die anderen Fenster
 * geschlossen sind (Versionszaun, N1.12).
 */
export type Bereich = 'zaehler' | 'vorgang' | 'meta' | 'puffer';

export interface SpeicherTx {
  get<T>(bereich: Bereich, schluessel: string): Promise<T | undefined>;
  put(bereich: Bereich, schluessel: string, wert: unknown): Promise<void>;
  /** Wie put, scheitert aber, wenn der Schlüssel schon belegt ist (nie überschreiben). */
  add(bereich: Bereich, schluessel: string, wert: unknown): Promise<void>;
  delete(bereich: Bereich, schluessel: string): Promise<void>;
  /** Alle Werte eines Bereichs (Reihenfolge der Schlüssel). */
  alle<T>(bereich: Bereich): Promise<T[]>;
}

export interface GeraeteSpeicher {
  readonly art: 'indexeddb' | 'lokal';
  /**
   * Führt `arbeit` atomar aus: Alle Änderungen gelten gemeinsam oder gar nicht. In der Arbeit
   * nur über `tx` lesen/schreiben (keine fremden await – IndexedDB schlösse die Transaktion).
   */
  transaktion<T>(arbeit: (tx: SpeicherTx) => Promise<T>): Promise<T>;
}

export class SpeicherBlockiert extends Error {
  constructor() {
    super(
      'Andere Fenster dieser Kasse sind noch mit einer älteren Version geöffnet – bitte alle anderen Fenster schließen',
    );
  }
}

export class SchluesselBelegt extends Error {
  constructor(bereich: string, schluessel: string) {
    super(`Eintrag ${bereich}/${schluessel} ist bereits vorhanden`);
  }
}

export const DB_NAME = 'bilanx-kassa';
export const DB_VERSION = 2;
export const PUFFER_STORE = 'offline-belege';
const BEREICHE: readonly Exclude<Bereich, 'puffer'>[] = ['zaehler', 'vorgang', 'meta'];
/** Objektspeicher je Bereich; der Puffer nutzt den bestehenden Speicher (Schlüssel im Wert). */
function store(b: Bereich): string {
  return b === 'puffer' ? PUFFER_STORE : b;
}

let veraltetMelden: (() => void) | null = null;
/** Rückruf, wenn eine neuere Version die Datenbank übernimmt (dieser Tab verkauft nicht mehr). */
export function beiVeraltetemSpeicher(rueckruf: () => void): void {
  veraltetMelden = rueckruf;
}

const offeneDbs = new WeakMap<IDBFactory, Promise<IDBDatabase>>();

/** Gemeinsames Öffnen der Kassen-Datenbank (Puffer und Gerätespeicher) in Version 2. */
export function oeffneKassenDb(indexedDb: IDBFactory): Promise<IDBDatabase> {
  let db = offeneDbs.get(indexedDb);
  if (db === undefined) {
    db = new Promise<IDBDatabase>((loese, lehneAb) => {
      const req = indexedDb.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const d = req.result;
        if (!d.objectStoreNames.contains(PUFFER_STORE)) {
          d.createObjectStore(PUFFER_STORE, { keyPath: 'schluessel' });
        }
        for (const b of BEREICHE) {
          if (!d.objectStoreNames.contains(b)) {
            d.createObjectStore(b);
          }
        }
      };
      req.onblocked = () => {
        lehneAb(new SpeicherBlockiert());
      };
      req.onsuccess = () => {
        const d = req.result;
        // Eine neuere Version will übernehmen: schließen und nicht weiter verkaufen
        d.onversionchange = () => {
          d.close();
          offeneDbs.delete(indexedDb);
          veraltetMelden?.();
        };
        loese(d);
      };
      req.onerror = () => {
        lehneAb(req.error ?? new Error('IndexedDB nicht verfügbar'));
      };
    });
    // Ein gescheitertes Öffnen (z. B. blockiert) später erneut versuchen
    db.catch(() => {
      offeneDbs.delete(indexedDb);
    });
    offeneDbs.set(indexedDb, db);
  }
  return db;
}

function anfrage<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((loese, lehneAb) => {
    req.onsuccess = () => {
      loese(req.result);
    };
    req.onerror = (ereignis) => {
      // Fehler nicht als Transaktionsabbruch weiterreichen – der Aufrufer entscheidet
      ereignis.preventDefault();
      lehneAb(req.error ?? new Error('IndexedDB-Fehler'));
    };
  });
}

export class IndexedDbGeraeteSpeicher implements GeraeteSpeicher {
  readonly art = 'indexeddb' as const;

  constructor(private readonly indexedDb: IDBFactory) {}

  async transaktion<T>(arbeit: (tx: SpeicherTx) => Promise<T>): Promise<T> {
    const db = await oeffneKassenDb(this.indexedDb);
    const t = db.transaction([...BEREICHE, PUFFER_STORE], 'readwrite');
    const fertig = new Promise<void>((loese, lehneAb) => {
      t.oncomplete = () => {
        loese();
      };
      t.onabort = () => {
        lehneAb(t.error ?? new Error('IndexedDB-Transaktion abgebrochen'));
      };
    });
    // Der Puffer-Speicher hat den Schlüssel im Wert (keyPath 'schluessel'), die übrigen außerhalb
    const mitSchluessel = (b: Bereich, k: string, w: unknown) =>
      b === 'puffer' ? [{ ...(w as object), schluessel: k }] : [w, k];
    const ohneSchluessel = (b: Bereich, w: unknown): unknown => {
      if (b !== 'puffer' || typeof w !== 'object' || w === null) {
        return w;
      }
      const rest: Record<string, unknown> = { ...(w as Record<string, unknown>) };
      delete rest.schluessel;
      return rest;
    };
    const tx: SpeicherTx = {
      get: async <W>(b: Bereich, k: string) =>
        ohneSchluessel(b, await anfrage(t.objectStore(store(b)).get(k))) as W | undefined,
      put: async (b, k, w) => {
        await anfrage(
          t.objectStore(store(b)).put(...(mitSchluessel(b, k, w) as [unknown, IDBValidKey?])),
        );
      },
      add: async (b, k, w) => {
        try {
          await anfrage(
            t.objectStore(store(b)).add(...(mitSchluessel(b, k, w) as [unknown, IDBValidKey?])),
          );
        } catch (e: unknown) {
          if (e instanceof DOMException && e.name === 'ConstraintError') {
            throw new SchluesselBelegt(b, k);
          }
          throw e;
        }
      },
      delete: async (b, k) => {
        await anfrage(t.objectStore(store(b)).delete(k));
      },
      alle: async <W>(b: Bereich) =>
        ((await anfrage(t.objectStore(store(b)).getAll())) as unknown[]).map(
          (w) => ohneSchluessel(b, w) as W,
        ),
    };
    let ergebnis: T;
    try {
      ergebnis = await arbeit(tx);
    } catch (e: unknown) {
      try {
        t.abort();
      } catch {
        // schon beendet
      }
      await fertig.catch(() => undefined);
      throw e;
    }
    await fertig;
    return ergebnis;
  }
}

/**
 * Speicher im Arbeitsspeicher mit Transaktionen (Tests: mehrere „Fenster“ teilen eine Instanz).
 * Transaktionen laufen nacheinander; eine gescheiterte Arbeit ändert nichts.
 */
export class SpeicherGeraeteSpeicher implements GeraeteSpeicher {
  readonly art = 'indexeddb' as const;
  private daten = new Map<string, unknown>();
  private kette: Promise<unknown> = Promise.resolve();
  /** Test: nächste Transaktion scheitert beim Abschluss (Speicherfehler). */
  naechsteScheitert = false;

  transaktion<T>(arbeit: (tx: SpeicherTx) => Promise<T>): Promise<T> {
    const lauf = this.kette
      .catch(() => undefined)
      .then(async () => {
        const kopie = new Map(this.daten);
        const k = (b: Bereich, s: string) => `${b}|${s}`;
        const tx: SpeicherTx = {
          get: <W>(b: Bereich, s: string) =>
            Promise.resolve(structuredClone(kopie.get(k(b, s))) as W | undefined),
          put: (b, s, w) => {
            kopie.set(k(b, s), structuredClone(w));
            return Promise.resolve();
          },
          add: (b, s, w) => {
            if (kopie.has(k(b, s))) {
              return Promise.reject(new SchluesselBelegt(b, s));
            }
            kopie.set(k(b, s), structuredClone(w));
            return Promise.resolve();
          },
          delete: (b, s) => {
            kopie.delete(k(b, s));
            return Promise.resolve();
          },
          alle: <W>(b: Bereich) =>
            Promise.resolve(
              [...kopie.entries()]
                .filter(([schl]) => schl.startsWith(`${b}|`))
                .sort(([a], [c]) => a.localeCompare(c))
                .map(([, w]) => structuredClone(w) as W),
            ),
        };
        const ergebnis = await arbeit(tx);
        if (this.naechsteScheitert) {
          this.naechsteScheitert = false;
          throw new Error('Speicherfehler (Test)');
        }
        this.daten = kopie;
        return ergebnis;
      });
    this.kette = lauf;
    return lauf;
  }

  /** Test: alles löschen (Browserdaten gelöscht). */
  leere(): void {
    this.daten.clear();
  }

  /** Test: einzelnen Eintrag entfernen bzw. beschädigen (Teilverlust). */
  setze(bereich: Bereich, schluessel: string, wert: unknown): void {
    if (wert === undefined) {
      this.daten.delete(`${bereich}|${schluessel}`);
    } else {
      this.daten.set(`${bereich}|${schluessel}`, wert);
    }
  }
}

interface LokaleAblage {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/** Ohne fensterübergreifende Sperre ist der localStorage-Rückfall nicht sicher (Codex N1-5). */
export class KeineSichereSpeicherung extends Error {
  constructor() {
    super(
      'Dieser Browser bietet keinen sicheren Gerätespeicher (IndexedDB bzw. Web Locks fehlen) – Verkauf hier nicht möglich',
    );
  }
}

type SperrFunktion = <T>(name: string, arbeit: () => Promise<T>) => Promise<T>;

function standardSperre(): SperrFunktion | null {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  return locks === undefined ? null : (name, arbeit) => locks.request(name, arbeit);
}

/**
 * Rückfall ohne IndexedDB: localStorage (nur online, kein Puffer). Der gesamte Zustand liegt
 * unter EINEM Schlüssel (Codex R2-7): Eine Transaktion liest ihn, arbeitet auf einer Kopie und
 * schreibt ihn mit einem einzigen `setItem` zurück – das ist der atomare Commit (scheitert er,
 * bleibt der alte Stand vollständig). Fensterübergreifend serialisiert eine Web-Lock-Sperre;
 * ohne Web Locks verweigert er jede Transaktion – die Kasse verkauft dann nicht (Codex N1-5).
 */
export const LOKAL_SCHLUESSEL = 'bilanx.geraetespeicher';

export class LokalerGeraeteSpeicher implements GeraeteSpeicher {
  readonly art = 'lokal' as const;

  constructor(
    private readonly ablage: LokaleAblage,
    private readonly sperre: SperrFunktion | null = standardSperre(),
  ) {}

  transaktion<T>(arbeit: (tx: SpeicherTx) => Promise<T>): Promise<T> {
    if (this.sperre === null) {
      return Promise.reject(new KeineSichereSpeicherung());
    }
    return this.sperre('bilanx.geraetespeicher', () => this.ohneSperre(arbeit));
  }

  private lesen(): Record<string, unknown> {
    const roh = this.ablage.getItem(LOKAL_SCHLUESSEL);
    if (roh === null) {
      return {};
    }
    const w: unknown = JSON.parse(roh); // unlesbar → Fehler, nie still leer beginnen
    if (typeof w !== 'object' || w === null || Array.isArray(w)) {
      throw new Error('Gerätespeicher (localStorage) unlesbar');
    }
    return w as Record<string, unknown>;
  }

  private async ohneSperre<T>(arbeit: (tx: SpeicherTx) => Promise<T>): Promise<T> {
    const stand = new Map(Object.entries(structuredClone(this.lesen())));
    const zustand = { geaendert: false };
    const k = (b: Bereich, s: string) => `${b}|${s}`;
    const tx: SpeicherTx = {
      get: <W>(b: Bereich, s: string) =>
        Promise.resolve(structuredClone(stand.get(k(b, s))) as W | undefined),
      put: (b, s, w) => {
        stand.set(k(b, s), structuredClone(w));
        zustand.geaendert = true;
        return Promise.resolve();
      },
      add: (b, s, w) => {
        if (stand.has(k(b, s))) {
          return Promise.reject(new SchluesselBelegt(b, s));
        }
        stand.set(k(b, s), structuredClone(w));
        zustand.geaendert = true;
        return Promise.resolve();
      },
      delete: (b, s) => {
        stand.delete(k(b, s));
        zustand.geaendert = true;
        return Promise.resolve();
      },
      alle: <W>(b: Bereich) =>
        Promise.resolve(
          [...stand.keys()]
            .filter((schl) => schl.startsWith(`${b}|`))
            .sort()
            .map((schl) => structuredClone(stand.get(schl)) as W),
        ),
    };
    const ergebnis = await arbeit(tx);
    // Atomarer Commit: genau ein Schreibvorgang (nur lesende Transaktionen schreiben nichts)
    if (zustand.geaendert) {
      this.ablage.setItem(LOKAL_SCHLUESSEL, JSON.stringify(Object.fromEntries(stand)));
    }
    return ergebnis;
  }
}

/**
 * Codex R3-6: tatsächliche Schreibfähigkeit nachweisen – Datenbank öffnen genügt nicht. Eine
 * echte Schreibtransaktion im gewählten Speicher (IndexedDB bzw. Rückfall mit Web-Lock-
 * Anforderung); scheitert sie, bleibt der Verkauf gestoppt.
 */
export async function pruefeSchreibfaehigkeit(
  speicher: GeraeteSpeicher,
  indexedDb: IDBFactory | null,
): Promise<void> {
  if (indexedDb !== null) {
    await oeffneKassenDb(indexedDb);
  }
  await speicher.transaktion((tx) => tx.put('meta', 'schreibprobe', new Date().toISOString()));
}
